import { useState, useCallback, useContext } from 'react';
import { toast } from 'react-toastify';
import navmenu from '~/config/navmenu';
import { useHistory } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { Context } from '~/context/auth';
import { useTelaPorCodigo } from './useTelaPorCodigo';

export interface Screen {
  label: string;
  content: any;
  active?: boolean;
  lib_icon: string;
  des_icon: string;
  cod_tela: number;
  url: string;
  linkMaterial: string;
  flg_abre_pesquisa: boolean;
}

export interface IUseNavigation {
  screens: Screen[];
  addScreen: (data: Screen) => void;
  removeScreen: (label: string | undefined) => void;
  removeAllScreens: () => void;
  loader: boolean;
  setScreenActive(cod_tela: any): void;
  navmenu: any;
}

export default function useNavigation(): IUseNavigation {
  const history = useHistory();
  const { isExpired } = useContext(Context);
  const [screens, setScreens] = useState<Screen[]>([]);
  const [loader, setLoader] = useState<boolean>(false);
  const queryClient = useQueryClient();

  const { fetchTelaPorCodigo } = useTelaPorCodigo();

  const addScreen = useCallback(
    async (data: Screen) => {
      if (isExpired && data.cod_tela !== 305) return;
      setLoader(true);
      if (screens.length >= 6 && data.cod_tela !== 305) {
        const filteredScreens = screens.find(
          (item) => item.cod_tela === data.cod_tela,
        );
        if (!filteredScreens) {
          history.goBack();
          toast.warn(
            'Limite de telas abertas esgotado, feche uma das telas para poder abrir',
          );
          setLoader(false);

          return;
        }
      }

      const removeActives = screens.map((screen) => ({
        ...screen,
        active: false,
      }));

      const contemHome: any = screens.filter((telaActive) => {
        return Number(telaActive.cod_tela) === 204;
      });

      const InfoTelaHome: any = navmenu.filter((tela: any) => {
        return tela.id === 204;
      });

      const telaArray = [];
      if (contemHome.length === 0 && Number(data.cod_tela) !== 204) {
        await fetchTelaPorCodigo(204).then((dataTela: any) => {
          const tela = {
            cod_tela: InfoTelaHome[0].id,
            content: InfoTelaHome[0].content,
            des_icon: dataTela[0].des_icon,
            label: dataTela[0].label_menu,
            lib_icon: dataTela[0].lib_icon,
            url: InfoTelaHome[0].url,
            linkMaterial: dataTela[0].link_material,
            flg_abre_pesquisa: dataTela[0].flg_abre_pesquisa,
            des_tela: dataTela[0].des_tela,
          };

          telaArray.push({ ...tela, active: false });
        });
      }

      const verifyOpenPage = screens.findIndex(
        (screen) => screen.cod_tela === data.cod_tela,
      );

      if (verifyOpenPage > -1) {
        removeActives[verifyOpenPage].active = true;
        setScreens(removeActives);
      } else if (screens.length === 0) {
        telaArray.push({ ...data, active: true });
        setScreens(telaArray);
      } else {
        setScreens([...removeActives, { ...data, active: true }]);
      }

      setLoader(false);
    },
    [screens],
  );

  const removeScreen = useCallback(
    (label: string | undefined) => {
      setLoader(true);
      // Excluir Screen
      const filtered = screens.filter(
        (screen: Screen) => screen.label !== label,
      );
      // Se existe filtrado
      const existActiveIndex = filtered.findIndex(
        (screen: Screen) => screen.active === true,
      );
      // Index filtrado
      const MaxScreen = filtered.length > 0 ? filtered.length - 1 : 0;
      const IndexActiveScreen =
        existActiveIndex !== -1 ? existActiveIndex : MaxScreen;
      // Removendo Ativos
      const removeActives = filtered.map((screen) => ({
        ...screen,
        active: false,
      }));
      // Ativando
      if (filtered.length >= 1) {
        removeActives[IndexActiveScreen].active = true;
        history.push(removeActives[IndexActiveScreen].url);
      }
      setScreens(removeActives);
      setLoader(false);
      if (filtered.length <= 0) {
        history.push('/app');
      }
    },
    [history, screens],
  );

  const removeAllScreens = useCallback(() => {
    setLoader(true);
    const homeScreen = screens.filter((screen: Screen) => screen.url === 'app');

    screens.forEach((screen) => {
      if (screen.url !== 'app') {
        queryClient.removeQueries(`cache_tela_${screen.cod_tela}`);
        queryClient.removeQueries(`infoCardAtivo_${screen.cod_tela}`);
        queryClient.removeQueries(`cacheLastSearch_${screen.cod_tela}`);
      }
    });

    setScreens(homeScreen);
    setLoader(false);
    if (!homeScreen[0]?.active) {
      history.push('/app');
    }
  }, [screens]);

  const setScreenActive = useCallback(
    (cod_tela) => {
      const removeActives = screens.map((screen) => ({
        ...screen,
        active: false,
      }));

      const indexPage = screens.findIndex(
        (screen) => screen.cod_tela === cod_tela,
      );

      removeActives[indexPage].active = true;
      history.push(removeActives[indexPage].url);
      setScreens(removeActives);
    },
    [screens],
  );

  return {
    screens,
    addScreen,
    removeScreen,
    removeAllScreens,
    loader,
    setScreenActive,
    navmenu,
  };
}
